import { memo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"

import {
  flag,
  setFlaggedFilter,
  setStarredFilter,
  setTypeFilter,
  starred,
  type,
} from "rtk-slice"

import { isMobile } from "utils/mediaQuery"

import {
  Divider,
  Grid,
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material"

import { snippetTypes } from "icons-symbols/snippetTypes"
import {
  Flagged,
  NAIcon,
  Starred,
  UnFlagged,
  UnStarred,
} from "ui-icons"

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(
  ({ theme }) => ({
    "& .MuiToggleButtonGroup-grouped": {
      margin: theme.spacing(isMobile() ? 0.25 : 1),
      border: 0,
    },
  })
)

const FlaggedToggleButtons = () => {
  const flaggedState = useSelector(flag)
  const dispatch = useDispatch()
  const handleToggleCompleted = useCallback(
    (e, newVal) => {
      dispatch(setFlaggedFilter(newVal))
    },
    [dispatch]
  )
  return (
    <StyledToggleButtonGroup
      value={flaggedState}
      onChange={handleToggleCompleted}
      aria-label="find snippets flagged or unflagged"
      exclusive
      size="small"
    >
      <ToggleButton value="not" aria-label="Unflagged snippets">
        <UnFlagged />
      </ToggleButton>
      <ToggleButton value="flagged" aria-label="Flagged snippets">
        <Flagged fill="#00c853" />
      </ToggleButton>
    </StyledToggleButtonGroup>
  )
}

const StarredToggleButtons = () => {
  const starredState = useSelector(starred)
  const dispatch = useDispatch()
  const handleToggleStarred = useCallback(
    (e, newVal) => {
      dispatch(setStarredFilter(newVal))
    },
    [dispatch]
  )
  return (
    <StyledToggleButtonGroup
      value={starredState}
      onChange={handleToggleStarred}
      aria-label="starred snippets"
      exclusive
      size="small"
    >
      <ToggleButton value="not" aria-label="not starred">
        <UnStarred />
      </ToggleButton>
      <ToggleButton value="starred" aria-label="starred">
        <Starred />
      </ToggleButton>
    </StyledToggleButtonGroup>
  )
}

const TypeToggleButtons = () => {
  const {
    palette: {
      primary: { main },
    },
  } = useTheme()
  const typeState = useSelector(type)
  const dispatch = useDispatch()
  const handleToggleType = (e, newVal) => {
    dispatch(setTypeFilter(newVal === "na" ? null : newVal || ""))
  }
  return (
    <StyledToggleButtonGroup
      value={typeState}
      onChange={handleToggleType}
      aria-label="find snippets by type"
      exclusive
      size="small"
    >
      <ToggleButton
        key={"na"}
        value="na"
        aria-label={`N/A snippets`}
        sx={{ p: "0.25em" }}
        selected={typeState === null}
      >
        <NAIcon width="34" height="25" color={main} />
      </ToggleButton>
      {snippetTypes.map((link) => (
        <ToggleButton
          key={link.title}
          value={link.title}
          aria-label={`${link.title} snippets`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {link.icon.value}
        </ToggleButton>
      ))}
    </StyledToggleButtonGroup>
  )
}

const DividerComponent = () => (
  <Grid item sx={{ alignSelf: "stretch", mx: "0.5em" }}>
    <Divider orientation="vertical" />
  </Grid>
)

const SnippetTypesFilterButtons = () => {
  const Flagged = memo(FlaggedToggleButtons)
  const Starred = memo(StarredToggleButtons)
  const Type = memo(TypeToggleButtons)

  return (
    <Stack
      direction="row"
      width="100%"
      justifyContent="center"
      alignItems="center"
      flex={1}
    >
      <Flagged />
      <DividerComponent />
      <Starred />
      <DividerComponent />
      <Type />
    </Stack>
  )
}

export default memo(SnippetTypesFilterButtons)
